import {
  ChangeEvent,
  FC,
  HTMLAttributes,
  memo,
  MouseEvent,
  useCallback,
  useMemo,
  useOptimistic,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Autocomplete,
  autocompleteClasses,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import classNames from 'classnames';

import { Button, Icon, Loading, TextField } from '@components/base';
import useLayoutEntityFilterContext from '@hooks/useLayoutEntityFilter';
import { LogicalOperator, LogicalOperators } from '@models/FilterDefinition';
import { stringComparator } from '@utils/arrayUtils';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  width: '100% !important',
  borderTop: `1px solid  ${theme.palette.divider}`,
  [`& .${autocompleteClasses.paper}`]: {
    height: 300,
    margin: 0,
    borderRadius: 0,
    boxShadow: 'none',
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: 'transparent',
    padding: 0,
    paddingTop: 0.5,
    height: '100%',
    [`& .${autocompleteClasses.option}`]: {
      padding: 0.25,
      fontSize: theme.typography.body2.fontSize,
      '&:not(:last-of-type)': {
        borderBottom: `1px solid  ${theme.palette.divider}`,
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
      '.fav-btn': {
        opacity: 0,
        '&.fav-tag, &:hover, &:focus, &:focus-visible, &:active': { opacity: 1 },
      },
      '&:hover, &:focus, &:focus-visible, &:focus-within, &:active': {
        '.fav-btn': { opacity: 1 },
      },
    },
  },
  [`& .${autocompleteClasses.noOptions}`]: {
    height: '100%',
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

type PopperComponentProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
};

export const SearchableListPopperComponent: FC<PopperComponentProps> = ({
  anchorEl: _anchorEl,
  disablePortal: _disablePortal,
  open: _open,
  ...rest
}) => <StyledAutocompletePopper {...rest} />;

export type TagFilterProps = {
  favoriteTags: string[];
  toggleFavorite: (tag: string, checked: boolean) => void;
};

const TagFilter: FC<TagFilterProps> = memo(({ favoriteTags, toggleFavorite }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [optimisticFavoriteTags, addOptimisticFavorite] = useOptimistic(
    favoriteTags,
    (state: string[], { checked, tag }: { checked: boolean; tag: string }) => {
      if (checked) {
        return state.includes(tag) ? state : [...state, tag];
      } else {
        return state.filter(t => t !== tag);
      }
    }
  );

  const handleToggleFavorite = useCallback(
    (tag: string, checked: boolean) => {
      addOptimisticFavorite({ checked, tag });
      toggleFavorite(tag, checked);
    },
    [addOptimisticFavorite, toggleFavorite]
  );

  const { t } = useTranslation();
  const { tagValues } = useLayoutEntityFilterContext();

  const tagOptions = useMemo(() => tagValues?.tagsLoad?.data ?? [], [tagValues?.tagsLoad?.data]);
  const tags = useMemo(() => tagValues?.tags ?? [], [tagValues?.tags]);

  const tagOptionsLoading = tagValues?.tagsLoad?.isLoading ?? false;
  const tagCombinator = tagValues?.tagCombinator ?? 'and';

  // Memoize callbacks from context to prevent breaking dependency arrays
  const setTags = useMemo(() => tagValues?.setTags ?? (() => {}), [tagValues?.setTags]);
  const setTagCombinator = useMemo(
    () => tagValues?.setTagCombinator ?? (() => {}),
    [tagValues?.setTagCombinator]
  );

  const sortedTagOptions = useMemo(() => [...tagOptions].sort(stringComparator), [tagOptions]);

  const handleTagChange = useCallback(
    (values: string[]) => {
      const newTags = Array.from(values).sort(stringComparator);
      setTags(newTags);
    },
    [setTags]
  );

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (anchorEl) return;

      setAnchorEl(event.currentTarget);

      if (tagOptions.length === 0 && !tagOptionsLoading) {
        tagValues?.tagsLoad?.refetch?.();
      }
    },
    [anchorEl, tagOptions.length, tagOptionsLoading, tagValues?.tagsLoad]
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleCombinatorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newCombinator = event.target.value as LogicalOperator;
      setTagCombinator(newCombinator);
    },
    [setTagCombinator]
  );

  const handleClearTags = useCallback(() => setTags([]), [setTags]);

  const renderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: string,
      { index, selected }: { index: number; selected: boolean }
    ) => {
      const isFavorite = optimisticFavoriteTags.includes(option);
      const { key, ...otherProps } = props as HTMLAttributes<HTMLLIElement> & {
        key?: string;
      };

      return (
        <li key={key} {...otherProps} data-option-index={index} data-option-value={option}>
          <Checkbox
            checked={selected}
            slotProps={{ input: { 'aria-label': `Select tag ${option}` } }}
          />

          <Typography component='span' flex={1} noWrap title={option}>
            {option}
          </Typography>

          <Tooltip
            placement='left'
            title={
              <Trans
                components={{ strong: <strong /> }}
                i18nKey={isFavorite ? 'tags.removeFavorite' : 'tags.addFavorite'}
                values={{ tag: option, context: 'formatted' }}
              />
            }
          >
            <Checkbox
              checked={isFavorite}
              checkedIcon={<Icon name='star' />}
              className={classNames('fav-btn', { 'fav-tag': isFavorite })}
              color='warning'
              icon={<Icon name='starOutline' />}
              onChange={e => handleToggleFavorite(option, e.target.checked)}
              onClick={e => e.stopPropagation()}
              slotProps={{
                input: {
                  'aria-label': t(isFavorite ? 'tags.removeFavorite' : 'tags.addFavorite', {
                    tag: option,
                  }),
                },
              }}
            />
          </Tooltip>
        </li>
      );
    },
    [optimisticFavoriteTags, handleToggleFavorite, t]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const { InputProps, ...otherParams } = params;
      return (
        <Stack p={1} spacing={1}>
          <TextField
            {...otherParams}
            placeholder={t('search')}
            slotProps={{
              input: {
                ...InputProps,
                'aria-label': t('tags'),
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon name='search' />
                  </InputAdornment>
                ),
                endAdornment: <span />,
              },
            }}
          />
        </Stack>
      );
    },
    [t]
  );

  const combinatorOptions = LogicalOperators.map(c => ({
    value: c,
    label: t('combinator', { context: c }),
  }));

  if (!tagValues) return null;

  const combinatorLabelId = 'tag-combinator-label';

  return (
    <Box display='flex'>
      <Button color='inherit' leftIcon='tag' onClick={handleOpen} rightIcon='chevronDown'>
        {t('tags')}
        {tags.length > 0 ? ` (${tags.length})` : ''}
      </Button>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={handleClose}
        open={!!anchorEl}
        slotProps={{ paper: { variant: 'elevation', elevation: 4 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {tagOptions.length === 0 && tagOptionsLoading ? (
          <Box p={5} width={300}>
            <Loading />
          </Box>
        ) : (
          <Stack maxWidth={1} minWidth={300} width={400}>
            <Box pt={1} px={2}>
              <FormControl>
                <FormLabel id={combinatorLabelId} sx={visuallyHidden}>
                  {t('combinator')}
                </FormLabel>

                <RadioGroup
                  aria-labelledby={combinatorLabelId}
                  onChange={handleCombinatorChange}
                  row
                  value={tagCombinator}
                >
                  {combinatorOptions.map(({ value, label }) => (
                    <FormControlLabel control={<Radio />} key={value} label={label} value={value} />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <Autocomplete
                disableClearable
                disableCloseOnSelect
                loading={tagOptionsLoading}
                multiple
                onChange={(_, values) => handleTagChange(values)}
                open
                options={sortedTagOptions}
                renderInput={renderInput}
                renderOption={renderOption}
                slots={{ popper: SearchableListPopperComponent }}
                value={tags}
              />
            </Box>

            <Divider />

            <Box display='flex' justifyContent='flex-end' p={1}>
              <Button disabled={tags.length === 0} leftIcon='close' onClick={handleClearTags}>
                {t('clear')}
              </Button>
            </Box>
          </Stack>
        )}
      </Popover>
    </Box>
  );
});

TagFilter.displayName = 'TagFilter';

export default TagFilter;
